import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { BarChart } from "../components/BarChart";
import { CardList, Empty, ErrorBox, PageHead, ResultBadge } from "../components/Bits";
import { DiffView } from "../components/DiffView";
import { Badge, Button, Card, Input, Select, Table } from "../design-system/components";
import { endpoints } from "../lib/api";
import { formatDate, formatDateTime, pct, recordText } from "../lib/format";

export function DeckDetailPage() {
  const { deckId = "" } = useParams();
  const client = useQueryClient();
  const detail = useQuery({ queryKey: ["deck", deckId], queryFn: () => endpoints.deck(deckId) });
  const analytics = useQuery({
    queryKey: ["analytics", deckId],
    queryFn: () => endpoints.analytics(deckId),
  });
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const latest = detail.data?.deck.latestVersion ?? 0;
  const toV = to === "" ? latest : Number.parseInt(to, 10);
  const fromV = from === "" ? Math.max(1, toV - 1) : Number.parseInt(from, 10);
  const diff = useQuery({
    queryKey: ["deck-diff", deckId, fromV, toV],
    queryFn: () => endpoints.deckDiff(deckId, fromV, toV),
    enabled: latest > 1 && fromV !== toV,
  });
  const patch = useMutation({
    mutationFn: (body: { name?: string; archived?: boolean }) => endpoints.patchDeck(deckId, body),
    onSuccess: () => {
      setRenaming(false);
      void client.invalidateQueries();
    },
  });
  if (detail.error) return <ErrorBox error={detail.error} />;
  const d = detail.data;
  if (!d) return <div className="pr-empty">Loading…</div>;
  const stats = analytics.data?.decks.find((x) => x.deckId === deckId);
  const latestVersion = d.versions.find((v) => v.version === latest);
  const versionOptions = d.versions.map((v) => ({
    value: String(v.version),
    label: `v${v.version} · ${formatDate(v.createdAt)}`,
  }));

  return (
    <div className="rk-stack">
      <PageHead
        title={d.deck.name}
        subtitle={`${d.deck.record.games} games · ${recordText(d.deck.record)} · v${latest}${d.deck.tcglDeckId ? " · linked to TCG Live deck" : ""}`}
        actions={
          <>
            {d.deck.archived && <Badge variant="completed">archived</Badge>}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setName(d.deck.name);
                setRenaming(true);
              }}
            >
              Rename
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => patch.mutate({ archived: !d.deck.archived })}
            >
              {d.deck.archived ? "Unarchive" : "Archive"}
            </Button>
          </>
        }
      />
      {renaming && (
        <Card>
          <div className="pr-inline">
            <div style={{ flex: 1, minWidth: 200 }}>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <Button
              size="sm"
              onClick={() => patch.mutate({ name })}
              disabled={name.trim().length === 0}
            >
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRenaming(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}
      <div className="rk-statgrid">
        <Card title="Win rate">
          <div className="rk-big">{pct(stats?.winRate)}</div>
          <div className="rk-lbl">{stats ? recordText(stats.record) : ""}</div>
        </Card>
        <Card title="Going first">
          <div className="rk-big">{stats ? recordText(stats.firstRecord) : "—"}</div>
          <div className="rk-lbl">second {stats ? recordText(stats.secondRecord) : "—"}</div>
        </Card>
        <Card title="Avg. turns">
          <div className="rk-big">{stats?.avgTurns ?? "—"}</div>
        </Card>
        <Card title="Versions">
          <div className="rk-big">{d.versions.length}</div>
          <div className="rk-lbl">
            {latestVersion?.complete ? "latest is complete" : "latest is partial"}
          </div>
        </Card>
      </div>
      <div className="rk-split rk-split--wide">
        <Card title="Record by version" description="Games and wins for each version of the list.">
          {stats && stats.byVersion.length > 0 ? (
            <BarChart
              ariaLabel="Games and wins per deck version"
              bars={stats.byVersion.map((v) => ({
                label: `v${v.version}`,
                value: v.record.games,
                secondary: v.record.wins,
              }))}
              height={140}
            />
          ) : (
            <Empty title="No games on this deck yet" />
          )}
        </Card>
        <Card title="Matchups">
          {stats && stats.topOpponents.length > 0 ? (
            <ul className="pr-list">
              {stats.topOpponents.map((o) => (
                <li key={o.archetype}>
                  <span>{o.archetype}</span>
                  <span>{recordText(o.record)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="pr-muted" style={{ fontSize: 14 }}>
              No matchups yet.
            </p>
          )}
        </Card>
      </div>
      <div className="rk-split">
        <Card
          title={`Current list · v${latest}`}
          description={
            latestVersion
              ? `${latestVersion.cardCount} cards · ${latestVersion.complete ? "complete" : "partial, inferred from games"} · ${latestVersion.source}`
              : undefined
          }
        >
          <CardList cards={latestVersion?.cards} />
        </Card>
        <Card
          title="Compare versions"
          description={
            latest > 1
              ? "What changed between two versions."
              : "A second version appears when the list changes."
          }
        >
          {latest > 1 && (
            <div className="rk-stack" style={{ gap: 12 }}>
              <div className="pr-inline">
                <div style={{ flex: 1 }}>
                  <Select
                    size="sm"
                    value={String(fromV)}
                    onChange={setFrom}
                    options={versionOptions}
                  />
                </div>
                <span className="pr-muted">→</span>
                <div style={{ flex: 1 }}>
                  <Select size="sm" value={String(toV)} onChange={setTo} options={versionOptions} />
                </div>
              </div>
              {diff.error && <ErrorBox error={diff.error} />}
              {diff.data && <DiffView diff={diff.data.diff} />}
            </div>
          )}
        </Card>
      </div>
      <Card title="Version history" description="Each version records the games played with it.">
        <div className="pr-timeline">
          {[...d.versions].reverse().map((v) => (
            <div key={v.version} className="pr-timeline__item">
              <div className="pr-timeline__v">v{v.version}</div>
              <div>
                <div className="pr-inline">
                  <span>{formatDateTime(v.createdAt)}</span>
                  <Badge variant={v.complete ? "success" : "warning"}>
                    {v.complete ? "complete" : "partial"}
                  </Badge>
                  <Badge variant="outline">{v.source}</Badge>
                  <span className="pr-muted">{recordText(v.record)}</span>
                  {v.sourceGameId && (
                    <Link to={`/games/${v.sourceGameId}`} style={{ fontSize: 13 }}>
                      from game
                    </Link>
                  )}
                </div>
                {v.diffFromPrevious && (
                  <div style={{ marginTop: 8 }}>
                    <DiffView diff={v.diffFromPrevious} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Games with this deck" flush>
        {d.games.length === 0 ? (
          <Empty title="No games yet" />
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Played</th>
                <th>Opponent</th>
                <th>Result</th>
                <th className="num">Version</th>
                <th className="num">Turns</th>
              </tr>
            </thead>
            <tbody>
              {d.games.map((g) => (
                <tr key={g.gameId}>
                  <td>
                    <Link to={`/games/${g.gameId}`}>{formatDateTime(g.playedAt)}</Link>
                  </td>
                  <td>
                    {g.opponentName ?? "?"}{" "}
                    {g.opponentArchetype ? (
                      <Badge variant="outline">{g.opponentArchetype}</Badge>
                    ) : null}
                  </td>
                  <td>
                    <ResultBadge game={g} />
                  </td>
                  <td className="num">v{g.deckVersion ?? "—"}</td>
                  <td className="num">{g.turnCount}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
